import type { Client } from './types';

/**
 * OrbitModules flag values, mirroring the backend `[Flags] enum OrbitModules`
 * (api/CommonApi/OrbitModules.cs). A client's enabled modules arrive as a bitmask
 * on `Client.modules`. Only the two products Orbit currently surfaces are listed.
 */
export const OrbitModule = {
    LeakDetection: 1,
    Savi: 32,
} as const;

export type OrbitModuleValue = (typeof OrbitModule)[keyof typeof OrbitModule];

/** True when the client's module bitmask includes the given module flag. */
export function clientHasModule(client: Client | null, module: OrbitModuleValue): boolean {
    return client != null && (client.modules & module) === module;
}

/**
 * Maps each Orbit product (top-level route segment) to the module a client must
 * have for that product to be usable. Used by the route guards to decide whether
 * to render the product or the upsell dialog.
 */
export const PRODUCT_MODULE = {
    'find-and-fix': OrbitModule.LeakDetection,
    savi: OrbitModule.Savi,
} as const;

export type ProductKey = keyof typeof PRODUCT_MODULE;

/**
 * Products in priority order (most primary first), each with the module it requires and the
 * route to navigate to. Used by ProductGuard to pick a fallback product when the active client
 * lacks the current product's module.
 */
export const PRODUCTS = [
    { key: 'find-and-fix', module: OrbitModule.LeakDetection, to: '/find-and-fix' },
    { key: 'savi', module: OrbitModule.Savi, to: '/savi' },
] as const;

export type Product = (typeof PRODUCTS)[number];

/** The first product (by PRODUCTS order) whose module the client has, or undefined if none. */
export function firstAvailableProduct(client: Client | null): Product | undefined {
    return PRODUCTS.find((p) => clientHasModule(client, p.module));
}

/** The product owning the given pathname (its first path segment), or null (e.g. the `/` landing). */
export function productKeyFromPathname(pathname: string): ProductKey | null {
    return PRODUCTS.find((p) => pathname === p.to || pathname.startsWith(`${p.to}/`))?.key ?? null;
}
